import { ref, computed, watch } from '@vue/composition-api';
import { useForm } from '@/composables';
import { createSurveyQuestionComment, deleteSurveyQuestionComment, updateSurveyQuestionComment, updateSurveyUserQuestionStatus, SurveyUserStatus, submitSurveyAnswers, getUserSurveysList, } from '@/api/v2/requests/surveys';
import { useAuthUser } from '@/composables';
import { surveyStatusOptionStyles } from '@/views/Brand/Surveys/_constants/survey-status';
const EMPTY_FORM = {
    surveyUserId: '',
    questionIdx: 0,
    content: '',
};
export function useSurveyQuestionModal(props, ctx) {
    const referenceForm = ref({ ...EMPTY_FORM });
    const { form } = useForm(referenceForm);
    const isSubmitting = ref(false);
    const { authUserId } = useAuthUser();
    const comments = ref([]);
    const isLoadingComments = ref(false);
    const isDeletingComment = ref(false);
    const editingComment = ref(null);
    const answer = ref(null);
    const wasAnswerEdited = ref(false);
    const attachmentFile = ref(null);
    const wasAttachmentEdited = ref(false);
    const statusOptions = computed(() => [
        {
            value: SurveyUserStatus.IN_VERIFICATION,
            label: ctx.root.$t('survey-status.IN_VERIFICATION'),
            style: surveyStatusOptionStyles[SurveyUserStatus.IN_VERIFICATION],
        },
        {
            value: SurveyUserStatus.REJECTED,
            label: ctx.root.$t('survey-status.REJECTED'),
            style: surveyStatusOptionStyles[SurveyUserStatus.REJECTED],
        },
        {
            value: SurveyUserStatus.APPROVED,
            label: ctx.root.$t('survey-status.APPROVED'),
            style: surveyStatusOptionStyles[SurveyUserStatus.APPROVED],
        },
    ]);
    const findStatusOption = (statusValue) => {
        if (!statusValue)
            return null;
        return (statusOptions.value.find((option) => option.value === statusValue) || null);
    };
    const selectedStatus = ref(findStatusOption(props.questionStatus));
    const surveyId = computed(() => ctx.root.$route.params.surveyId);
    watch(() => props.answer, () => {
        answer.value = props.answer;
        wasAnswerEdited.value = false;
    }, { immediate: true });
    watch(() => props.questionFile, () => {
        attachmentFile.value = props.questionFile;
        wasAttachmentEdited.value = false;
    }, { immediate: true });
    const commentsUrl = computed(() => {
        if (!props.surveyUserId)
            return '';
        return `/surveys/comments/${props.surveyUserId}`;
    });
    const commentsQuery = computed(() => ({
        questionIdx: props.questionIndex,
        sort: '-createdAt',
    }));
    const shouldFetchComments = ref(true);
    const forceUpdateComments = ref(0);
    const questionType = computed(() => props.question && props.question.type ? props.question.type : '');
    const questionName = computed(() => props.question && props.question.name ? props.question.name : '');
    const isContentEmpty = computed(() => {
        if (!form.value.content)
            return true;
        const textContent = form.value.content
            .replace(/<[^>]*>/g, '')
            .replace(/&nbsp;/g, ' ')
            .trim();
        return textContent === '';
    });
    const hasStatusChanged = computed(() => {
        const currentStatusValue = selectedStatus.value?.value;
        const initialStatusValue = props.questionStatus;
        return currentStatusValue !== initialStatusValue;
    });
    const resetForm = () => {
        form.value = { ...EMPTY_FORM };
        editingComment.value = null;
        selectedStatus.value = findStatusOption(props.questionStatus);
    };
    const updateFormData = () => {
        form.value = {
            surveyUserId: props.surveyUserId || '',
            questionIdx: props.questionIndex,
            content: '',
        };
        selectedStatus.value = findStatusOption(props.questionStatus);
    };
    const handleCommentsLoaded = (loadedComments) => {
        comments.value = loadedComments;
    };
    const refreshComments = () => {
        forceUpdateComments.value += 1;
    };
    const scrollToBottom = () => {
        ctx.root.$nextTick(() => {
            const container = document.getElementById('comments-container');
            if (container) {
                container.scrollTop = container.scrollHeight;
            }
        });
    };
    const fetchComments = async () => {
        refreshComments();
    };
    const handleDeleteComment = async (comment) => {
        isDeletingComment.value = true;
        try {
            await deleteSurveyQuestionComment(comment.id);
            ctx.root.$toast.success(ctx.root.$t('brand-surveys.survey-question-modal.comment-deleted-success'));
            refreshComments();
            ctx.emit('comment-deleted');
        }
        catch (error) {
            ctx.root.$toast.error(ctx.root.$t('brand-surveys.survey-question-modal.comment-deleted-error'));
        }
        finally {
            isDeletingComment.value = false;
        }
    };
    const handleEditComment = (comment) => {
        editingComment.value = comment;
        form.value.content = comment.content;
    };
    const cancelEdit = () => {
        editingComment.value = null;
        form.value.content = '';
    };
    const updateStatus = async () => {
        if (!props.surveyUserId ||
            !hasStatusChanged.value ||
            !selectedStatus.value) {
            return;
        }
        const statusValue = selectedStatus.value.value;
        try {
            await updateSurveyUserQuestionStatus(props.surveyUserId, String(props.questionIndex), { status: statusValue });
            ctx.root.$toast.success(ctx.root.$t('brand-surveys.survey-question-modal.status-updated-success') || 'Status updated.');
            ctx.emit('status-change', statusValue, {
                surveyUserId: props.surveyUserId,
                questionIndex: props.questionIndex,
            });
        }
        catch (e) {
            selectedStatus.value = findStatusOption(props.questionStatus);
            ctx.root.$toast.error(ctx.root.$t('brand-surveys.survey-question-modal.status-updated-error'));
            throw e;
        }
    };
    const handleSubmitComment = async () => {
        if (isContentEmpty.value || !surveyId.value)
            return;
        try {
            if (editingComment.value) {
                await updateSurveyQuestionComment(editingComment.value.id, {
                    content: form.value.content,
                });
                ctx.root.$toast.success(ctx.root.$t('brand-surveys.survey-question-modal.comment-updated-success'));
                ctx.emit('comment-updated');
                editingComment.value = null;
            }
            else {
                await createSurveyQuestionComment(surveyId.value, {
                    surveyUserId: props.surveyUserId,
                    questionIdx: form.value.questionIdx,
                    content: form.value.content,
                });
                ctx.root.$toast.success(ctx.root.$t('brand-surveys.survey-question-modal.comment-added-success'));
                ctx.emit('comment-created');
            }
            refreshComments();
            form.value.content = '';
            setTimeout(() => {
                scrollToBottom();
            }, 300);
        }
        catch (error) {
            const errorKey = editingComment.value
                ? 'brand-surveys.survey-question-modal.comment-updated-error'
                : 'brand-surveys.survey-question-modal.comment-added-error';
            ctx.root.$toast.error(ctx.root.$t(errorKey));
            throw error;
        }
    };
    const handleChangeAnswer = (newAnswer) => {
        answer.value = newAnswer;
        wasAnswerEdited.value = true;
    };
    const handleAttachmentChange = (newAttachment) => {
        attachmentFile.value = newAttachment;
        wasAttachmentEdited.value = true;
    };
    const handleSubmit = async () => {
        if (isContentEmpty.value &&
            !hasStatusChanged.value &&
            !wasAnswerEdited.value &&
            !wasAttachmentEdited.value) {
            return;
        }
        isSubmitting.value = true;
        try {
            let dataChanged = false;
            let updatedFiles = null;
            let updatedAnswers = null;
            if (hasStatusChanged.value) {
                await updateStatus();
                dataChanged = true;
            }
            if (!isContentEmpty.value) {
                await handleSubmitComment();
            }
            const currentAnswers = props.answers || props.surveyUser?.answers;
            const targetUserId = props.surveyUser?.user?.id || authUserId.value;
            if ((wasAnswerEdited.value || wasAttachmentEdited.value) &&
                currentAnswers &&
                targetUserId) {
                const answers = [...currentAnswers];
                if (wasAnswerEdited.value) {
                    answers[props.questionIndex] = answer.value;
                }
                let files;
                if (props.surveyUser?.files) {
                    files = [...props.surveyUser.files];
                }
                else {
                    files = new Array(answers.length).fill('');
                }
                if (wasAttachmentEdited.value) {
                    files[props.questionIndex] = attachmentFile.value;
                }
                await submitSurveyAnswers(surveyId.value, answers, files, undefined, targetUserId, props.placeId || '');
                const { nodes } = await getUserSurveysList({
                    userId: targetUserId,
                    surveyId: surveyId.value,
                    placeId: props.placeId || '',
                });
                updatedFiles = nodes.length > 0 ? nodes[0].files : files;
                updatedAnswers = nodes.length > 0 ? nodes[0].answers : answers;
                ctx.root.$toast.success(ctx.root.$t('brand-surveys.survey-question-modal.answer-updated-success'));
                dataChanged = true;
            }
            if (dataChanged) {
                ctx.emit('data-updated', {
                    questionIndex: props.questionIndex,
                    newAnswer: answer.value,
                    newFiles: updatedFiles,
                    surveyUserId: props.surveyUserId,
                });
            }
        }
        finally {
            isSubmitting.value = false;
        }
    };
    const handleClose = () => {
        resetForm();
        comments.value = [];
        ctx.emit('close');
    };
    const setupWatchers = () => {
        ctx.root.$watch(() => props.isVisible, (newValue) => {
            if (newValue) {
                updateFormData();
                shouldFetchComments.value = true;
            }
            else {
                resetForm();
                comments.value = [];
                shouldFetchComments.value = false;
            }
        });
        ctx.root.$watch(() => props.surveyUserId, () => {
            if (props.isVisible)
                refreshComments();
        });
        ctx.root.$watch(() => props.questionIndex, () => {
            if (props.isVisible)
                refreshComments();
        });
        ctx.root.$watch(() => props.questionStatus, (newStatus) => {
            selectedStatus.value = findStatusOption(newStatus);
        });
    };
    return {
        form,
        isSubmitting,
        comments,
        isLoadingComments,
        isDeletingComment,
        editingComment,
        selectedStatus,
        statusOptions,
        commentsUrl,
        commentsQuery,
        shouldFetchComments,
        forceUpdateComments,
        questionType,
        questionName,
        isContentEmpty,
        hasStatusChanged,
        handleClose,
        handleSubmit,
        handleDeleteComment,
        handleEditComment,
        cancelEdit,
        fetchComments,
        handleCommentsLoaded,
        scrollToBottom,
        setupWatchers,
        handleChangeAnswer,
        handleAttachmentChange,
    };
}
